import { useEffect, useMemo, useRef } from "react";
import QRCodeStyling from "qr-code-styling-new";

function createQrCode(variant: "orb" | "rtms") {
  if (variant === "rtms") {
    return new QRCodeStyling({
      type: "svg",
      qrOptions: { errorCorrectionLevel: "Q" },
      cornersSquareOptions: { type: "square", color: "#000000" },
      cornersDotOptions: { type: "square", color: "#000000" },
      dotsOptions: { type: "square", color: "#000000" },
      backgroundOptions: { color: "#ffffff" },
    });
  }
  return new QRCodeStyling({
    type: "svg",
    cornersSquareOptions: { type: "extra-rounded" },
    cornersDotOptions: { type: "dot" },
    dotsOptions: {
      color: "var(--colors-qrcode)",
      type: "extra-rounded",
    },
    backgroundOptions: { color: "transparent" },
    imageOptions: { margin: 4, hideBackgroundDots: true },
  });
}

export function OrbQRCode({
  content,
  label = "Orb capture QR code",
  variant = "orb",
}: {
  content: string;
  label?: string;
  variant?: "orb" | "rtms";
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const qrCode = useMemo(() => createQrCode(variant), [variant]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.replaceChildren();
    qrCode.append(container);

    const update = () => {
      const size = container.clientWidth;
      if (!size) return;
      qrCode.update({ data: content, width: size, height: size });
      if (variant === "rtms") {
        // The encoder exposes the module count after update. Reserve at least
        // four white modules on every side, including after responsive resizing.
        const modules = qrCode._qr?.getModuleCount();
        if (modules) {
          qrCode.update({ margin: Math.ceil((4 * size) / (modules + 8)) });
        }
      }
    };
    update();

    window.addEventListener("resize", update);
    window.addEventListener("focus", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("focus", update);
    };
  }, [content, qrCode, variant]);

  return (
    <div
      ref={containerRef}
      className="mx-auto w-[calc(min(100vw,100%)-64px)]"
      aria-label={label}
    />
  );
}
